import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import RemoveConnectedPharmacistModal from "./modals/RemoveConPharmModal";
import Loader from "../../common/Loader/Loader";
import APIVariables from "../../../services/api-variables";
import { handleCatch } from "../../../shared/helpers";
import { errorMessages } from "../../../constants/errorMessages.constants";
import { App } from "../../../routes/Constants";
import Axios from "../../../services/Axios";
import DataTable from "@/global/tables/DataTable";
import { getConnectedPharmacistsListColumns } from "./ConnectedPharmacistsListColumns";

const ConnectedPharmacistsList = () => {
  const [modalIsOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPharmacist, setSelectePharmacist] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [items, setItems] = useState([]);
  const navigate = useNavigate();

  const addPharmacist = () => {
    navigate(App.ADD_CONNECTED_PHARMACIST);
  };

  const fetchEntireConnectedPharmacistsList = async () => {
    setIsLoading(true);
    try {
      let acc = [];
      let nextUrl = APIVariables.HCP_CON_PHARMACISTS;

      while (nextUrl) {
        const res = await Axios.get(nextUrl);
        if (res.status !== 200) break;

        const { data } = res;

        if (Array.isArray(data)) {
          acc = data;
          nextUrl = null;
        } else {
          const chunk = data?.results ?? [];
          acc = acc.concat(chunk);
          nextUrl = data?.next || null;
        }
      }

      setItems(acc);
    } catch (error) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEntireConnectedPharmacistsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openModalRemovePharmacist = useCallback((e, id) => {
    e.stopPropagation();
    setSelectePharmacist(id);
    setIsModalOpen(true);
  }, []);

  const redirectToEditPharmacist = useCallback(
    (row) => {
      navigate(App.VIEW_CP_DEFAULT + `${row.id}`);
    },
    [navigate]
  );

  const closeModal = () => {
    setSelectePharmacist("");
    setErrorMessage("");
    setIsModalOpen(false);
  };

  const removePharmacist = async () => {
    setErrorMessage("");
    await Axios.delete(APIVariables.HCP_CON_PHARMACIST + selectedPharmacist)
      .then((res) => {
        const { status } = res;
        if (status === 200) {
          const newItems = items.filter((item) => {
            return item.id !== selectedPharmacist;
          });
          setItems(newItems);
          closeModal();
        }
      })
      .catch(function (error) {
        const errors = error.response.data;
        if (typeof errors === "string") {
          setErrorMessage([errorMessages.INTERNAL_SERVER_ERROR]);
        } else {
          const errorsArray = [];
          Object.keys(errors).forEach((key) => {
            errorsArray.push(key + " - " + errors[key]);
          });
          setErrorMessage(errorsArray);
        }
      });
  };

  const columns = useMemo(
    () => getConnectedPharmacistsListColumns({ onRemove: openModalRemovePharmacist }),
    [openModalRemovePharmacist]
  );

  return (
    <div
      className="min-h-screen overflow-y-auto pt-[140px] pb-[100px]"
      data-testid="connected-pharmacists-page"
    >
      {isLoading ? (
        <Loader />
      ) : (
        <div className="mx-auto max-w-[1100px] space-y-4">
          <div className="flex items-center justify-between py-2.5">
            <h1
              className="h1 mineShaft-color"
              data-testid="connected-pharmacists-heading"
            >
              Connected Pharmacists
            </h1>
            <button
              type="button"
              className="second-button"
              onClick={addPharmacist}
              data-testid="connected-pharmacists-add-button"
            >
              Add Pharmacist
            </button>
          </div>
          <div
            className="rounded-md border bg-white px-4 pb-4"
            data-testid="connected-pharmacists-table"
          >
            <DataTable
              columns={columns}
              data={items}
              tableTitle="Connected pharmacists"
              onRowClick={redirectToEditPharmacist}
              loading={false}
              getRowTestId={(row) => `connected-pharmacists-row-${row?.id}`}
            />
          </div>
          {modalIsOpen && (
            <RemoveConnectedPharmacistModal
              modalIsOpen={modalIsOpen}
              closeModal={closeModal}
              removePharmacist={removePharmacist}
              errorMessage={errorMessage}
            />
          )}
        </div>
      )}
    </div>
  );
};
export default ConnectedPharmacistsList;
